import {
  OutlinedSelect,
  SelectOptionType,
  TextOutlinedInput,
  Button,
} from '@octano/global-ui';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import { useCallback } from 'react';

import { TeacherFileFilters } from '../interfaces/teacher-file-filters.interface';
import { TranslationsKeys } from '../../../../../locales/translations';

type FormValues = {
  search: string | null;
  school: SelectOptionType | null;
  campus: SelectOptionType | null;
};

const DEFAULT_FORM_VALUES: FormValues = {
  search: '',
  school: null,
  campus: null,
};

const dictPrefix = 'searchControls';

interface Props {
  schools?: SelectOptionType[];
  campuses?: SelectOptionType[];
  loading?: boolean;
  onSearch?: (filters?: TeacherFileFilters) => void;
}

export function SearchControls({
  schools = [],
  campuses = [],
  loading = false,
  onSearch,
}: Props) {
  const { t } = useTranslation(
    TranslationsKeys.TEACHER_MANAGMENT_TEACHER_FILE_LIST,
  );

  const { control, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: DEFAULT_FORM_VALUES,
  });

  const handleSearch = useCallback(
    (values: FormValues) => {
      if (loading || !onSearch) {
        return;
      }
      if (
        values?.search?.trim() ||
        values?.school?.value ||
        values?.campus?.value
      ) {
        onSearch({
          search: values?.search?.trim() ? values?.search?.trim() : undefined,
          schoolId: values?.school?.value ?? undefined,
          campusId: values?.campus?.value ?? undefined,
        });
      } else {
        onSearch(undefined);
      }
    },
    [onSearch, loading],
  );

  const handleClear = useCallback(() => {
    if (loading) {
      return;
    }
    reset(DEFAULT_FORM_VALUES);
    !!onSearch && onSearch(undefined);
  }, [onSearch, reset, loading]);

  return (
    <Row className="mb-5">
      <Col xs={12} sm={6} md={6} lg={4}>
        <TextOutlinedInput
          control={control}
          name="search"
          label={t(`${dictPrefix}.search.label`)}
          placeholder={t(`${dictPrefix}.search.placeholder`)}
          disabled={loading}
        />
      </Col>
      <Col xs={12} sm={6} md={6} lg={4}>
        <OutlinedSelect
          control={control}
          name="school"
          label={t(`${dictPrefix}.school.label`)}
          placeholder={t(`${dictPrefix}.school.placeholder`)}
          options={schools ?? []}
          disabled={!schools?.length || loading}
        />
      </Col>
      <Col xs={12} sm={6} md={6} lg={4}>
        <OutlinedSelect
          control={control}
          name="campus"
          label={t(`${dictPrefix}.campus.label`)}
          placeholder={t(`${dictPrefix}.campus.placeholder`)}
          options={campuses ?? []}
          disabled={!campuses?.length || loading}
        />
      </Col>
      <Col xs={12}>
        <div className="d-flex flex-wrap w-100 align-items-center justify-content-center">
          <div className="flex-fill px-2" />
          <div>
            <div className="d-flex w-100 align-items-center" style={{ gap: 8 }}>
              <div className="flex-fill">
                <Button
                  size="md"
                  text={t(`${dictPrefix}.actions.search`)}
                  loading={loading}
                  onClick={handleSubmit(handleSearch)}
                  className="w-100"
                />
              </div>
              <div className="flex-fill">
                <Button
                  size="md"
                  text={t(`${dictPrefix}.actions.clear`)}
                  className="w-100"
                  loading={loading}
                  onClick={handleClear}
                  outlined
                />
              </div>
            </div>
          </div>
        </div>
      </Col>
    </Row>
  );
}
